import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Circle, Rocket, X, ArrowRight, Sparkle, Trophy } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OnboardingWidget - guide pas-a-pas pour nouvelles agences SaaS.
 *
 * - Widget compact bottom-right (montre la progression)
 * - Au clic : modal complet avec liste des etapes + CTA
 * - Detection auto des etapes (data-driven via /api/onboarding/status)
 * - Bouton "Plus tard" -> dismiss
 */
const OnboardingWidget = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API}/onboarding/status`, { withCredentials: true });
      setStatus(r.data);
    } catch (e) {/* user pas chef ou non logge */}
  };

  useEffect(() => {
    fetchStatus();
    // Refresh apres navigation (pour detecter les nouvelles actions du user)
    const onFocus = () => fetchStatus();
    window.addEventListener('focus', onFocus);
    // Feb 2026 — Espacé de 1min à 5min pour réduire les erreurs réseau
    // affichées par Chrome quand la connexion vacille.
    const interval = setInterval(fetchStatus, 300000);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
    };
  }, []);

  const dismiss = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/onboarding/dismiss`, {}, { withCredentials: true });
      setStatus({ ...status, dismissed: true });
      setOpen(false);
    } finally { setLoading(false); }
  };

  if (!status) return null;
  if (status.dismissed) return null;
  if (status.is_complete && status.completed === status.total) return null;

  const goTo = (route) => {
    setOpen(false);
    navigate(route);
  };

  const compactColor = status.is_complete ? 'from-emerald-500 to-teal-500' : 'from-violet-500 to-fuchsia-500';

  return (
    <>
      {/* Widget compact bottom-right */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`fixed bottom-5 right-5 z-40 group inline-flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-xl bg-gradient-to-br ${compactColor} text-white font-semibold hover:scale-105 transition-transform`}
        data-testid="onboarding-widget-btn"
      >
        <div className="relative">
          {status.is_complete ? <Trophy size={22} weight="fill" /> : <Rocket size={22} weight="fill" />}
          <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
          </span>
        </div>
        <div className="text-left">
          <div className="text-xs opacity-90">Demarrage rapide</div>
          <div className="text-sm font-bold leading-tight">{status.completed}/{status.total} etapes</div>
        </div>
      </button>

      {/* Modal complet */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="onboarding-modal"
          >
            {/* Header */}
            <div className={`px-7 py-5 bg-gradient-to-br ${compactColor} text-white relative`}>
              <button
                onClick={() => setOpen(false)}
                className="absolute top-4 right-4 p-1.5 rounded-lg bg-white/20 hover:bg-white/30"
              >
                <X size={18} />
              </button>
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-white/20">
                  {status.is_complete ? <Trophy size={28} weight="fill" /> : <Rocket size={28} weight="fill" />}
                </div>
                <div>
                  <h2 className="text-xl font-bold">
                    {status.is_complete ? 'Felicitations !' : 'Bienvenue sur votre CRM'}
                  </h2>
                  <p className="text-sm opacity-90 mt-0.5">
                    {status.tenant_name || 'Votre agence'}
                    {status.tenant_plan && ` · Plan ${status.tenant_plan}`}
                  </p>
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs mb-1.5 opacity-90">
                  <span>Progression : {status.progress_pct}%</span>
                  <span>{status.completed} / {status.total} etapes</span>
                </div>
                <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-white rounded-full transition-all duration-500"
                    style={{ width: `${status.progress_pct}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Liste des etapes */}
            <div className="flex-1 overflow-y-auto p-6">
              <ul className="space-y-3">
                {status.steps.map((step, idx) => (
                  <li
                    key={step.key}
                    className={`p-4 rounded-2xl border-2 transition-all ${
                      step.done
                        ? 'bg-emerald-50 border-emerald-200'
                        : 'bg-stone-50 border-stone-200 hover:border-violet-300 hover:bg-violet-50'
                    }`}
                    data-testid={`onboarding-step-${step.key}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="shrink-0 mt-0.5">
                        {step.done ? (
                          <CheckCircle size={28} weight="fill" className="text-emerald-500" />
                        ) : (
                          <Circle size={28} className="text-stone-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className={`font-bold ${step.done ? 'text-emerald-900 line-through' : 'text-stone-900'}`}>
                            {idx + 1}. {step.title}
                          </h4>
                          {!step.critical && (
                            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 bg-stone-200 text-stone-600 rounded">
                              optionnel
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-stone-600 mt-1">{step.description}</p>
                        {!step.done && (
                          <button
                            onClick={() => goTo(step.route)}
                            className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg transition-colors"
                            data-testid={`onboarding-step-cta-${step.key}`}
                          >
                            {step.cta} <ArrowRight size={14} weight="bold" />
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              {status.is_complete && (
                <div className="mt-6 p-4 bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl text-center">
                  <Sparkle size={32} weight="fill" className="text-emerald-500 mx-auto" />
                  <h3 className="font-bold text-emerald-900 mt-2">Tout est en place !</h3>
                  <p className="text-sm text-emerald-700 mt-1">
                    Votre agence est operationnelle. Bonne vente !
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-stone-100 bg-stone-50 flex items-center justify-between">
              <p className="text-xs text-stone-500">
                💡 Les etapes se cochent automatiquement quand vous les realisez.
              </p>
              <button
                onClick={dismiss}
                disabled={loading}
                className="text-sm text-stone-600 hover:text-stone-900 underline"
                data-testid="onboarding-dismiss-btn"
              >
                Plus tard
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OnboardingWidget;
